import React from 'react';

import { RestrictedAction } from '@components/shared/RestrictedAction';
import { useCreateStudy } from '@hooks/useCreateStudy';
import { useDisabledFeatures } from '@hooks/useDisabledFeatures';
import { useFeature } from '@hooks/useFeature';
import { usePlan } from '@hooks/usePlan';

import { NewStudyKind } from '../types';

import { StudyButton } from './StudyButton';

interface ButtonsProps {
  disabled?: boolean;
  teamId?: number | null;
  onClose?: () => void;
  externalCandidatesDefaulted?: boolean;
}

interface Props extends ButtonsProps {
  kind: NewStudyKind;
}

export const NewStudyButton: React.FC<React.PropsWithChildren<Props>> = ({
  kind,
  disabled: baseDisabled,
  teamId,
  onClose,
  externalCandidatesDefaulted
}) => {
  const { createStudy, isCreating } = useCreateStudy();
  const { hasFeature } = usePlan();
  const isDisabled = isCreating || baseDisabled;

  const focusGroupsEnabled = useFeature('focus_groups');
  const aiModerationEnabled = useFeature('ai_moderation');

  const unmoderatedTestsEnabled = hasFeature('unmoderated');

  const { disabledFeatures } = useDisabledFeatures();

  const handleOnClick = async (args: { style: Study['style']; template_key?: string } & Partial<Study>) => {
    const has_screener: boolean = externalCandidatesDefaulted ?? false;

    await createStudy({
      external_candidates_enabled: externalCandidatesDefaulted ?? false,
      has_screener,
      team_id: teamId,
      onSuccess: onClose,
      ...args
    });
  };

  switch (kind) {
    // Moderated
    case 'video_call':
      return (
        <StudyButton disabled={isDisabled} kind='video_call' onClick={() => handleOnClick({ style: 'video_call' })} />
      );

    case 'focus_groups':
      return focusGroupsEnabled ? (
        <RestrictedAction feature='focus_groups'>
          {({ may, can, show }) => (
            <StudyButton
              kind='focus_groups'
              disabled={isDisabled}
              onClick={() => {
                if (!may || !can) {
                  show?.();
                  return;
                }
                handleOnClick({ style: 'video_call', focus_group: true });
              }}
            />
          )}
        </RestrictedAction>
      ) : null;

    case 'round_robin':
      return (
        <RestrictedAction feature='round_robin'>
          {({ may, can, show }) => (
            <StudyButton
              kind='round_robin'
              disabled={isDisabled}
              onClick={() => {
                if (!may || !can) {
                  show?.();
                  return;
                }
                handleOnClick({ style: 'video_call', moderation_style: 'round_robin' });
              }}
            />
          )}
        </RestrictedAction>
      );

    case 'collective':
      return (
        <StudyButton
          kind='collective'
          disabled={isDisabled}
          onClick={() => handleOnClick({ style: 'video_call', moderation_style: 'collective' })}
        />
      );

    case 'continuous':
      return (
        <RestrictedAction feature='continuous'>
          {({ may, can, show }) => (
            <StudyButton
              kind='continuous'
              disabled={isDisabled}
              onClick={() => {
                if (!may || !can) {
                  show?.();
                  return;
                }
                handleOnClick({ style: 'video_call', continuous: true });
              }}
            />
          )}
        </RestrictedAction>
      );

    // Unmoderated
    case 'unmoderated_test':
      return unmoderatedTestsEnabled && !disabledFeatures.unmoderated_test ? (
        <StudyButton
          disabled={isDisabled}
          kind='unmoderated_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test' })}
        />
      ) : null;

    case 'prototype_test':
      return unmoderatedTestsEnabled && !disabledFeatures.unmoderated_test ? (
        <StudyButton
          disabled={isDisabled}
          kind='prototype_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test', template_key: 'prototype_test' })}
        />
      ) : null;

    case 'card_sort':
      return unmoderatedTestsEnabled && !disabledFeatures.unmoderated_test ? (
        <StudyButton
          disabled={isDisabled}
          kind='card_sort'
          onClick={() => handleOnClick({ style: 'unmoderated_test', template_key: 'card_sort' })}
        />
      ) : null;

    case 'tree_test':
      return unmoderatedTestsEnabled && !disabledFeatures.unmoderated_test ? (
        <StudyButton
          disabled={isDisabled}
          kind='tree_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test', template_key: 'tree_test' })}
        />
      ) : null;

    case 'ai_moderation':
      return unmoderatedTestsEnabled && aiModerationEnabled ? (
        <StudyButton
          showBadge
          disabled={isDisabled}
          kind='ai_moderation'
          onClick={() => handleOnClick({ style: 'unmoderated_test', template_key: 'ai_moderation' })}
        />
      ) : null;

    case 'five_second_test':
      return (
        <StudyButton
          tooltip='Coming soon.'
          showBadge='coming_soon'
          disabled
          kind='five_second_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test' })}
        />
      );
    case 'first_click_test':
      return (
        <StudyButton
          tooltip='Coming soon.'
          showBadge='coming_soon'
          disabled
          kind='first_click_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test' })}
        />
      );
    case 'preference_test':
      return (
        <StudyButton
          tooltip='Coming soon.'
          showBadge='coming_soon'
          disabled
          kind='preference_test'
          onClick={() => handleOnClick({ style: 'unmoderated_test' })}
        />
      );

    // Other
    case 'survey':
      return !disabledFeatures.survey ? (
        <StudyButton disabled={isDisabled} kind='survey' onClick={() => handleOnClick({ style: 'survey' })} />
      ) : null;

    case 'panel':
      return !externalCandidatesDefaulted && !disabledFeatures.panel ? (
        <StudyButton disabled={isDisabled} kind='panel' onClick={() => handleOnClick({ style: 'panel' })} />
      ) : null;

    case 'online_task':
      return !disabledFeatures.online_task ? (
        <StudyButton disabled={isDisabled} kind='online_task' onClick={() => handleOnClick({ style: 'online_task' })} />
      ) : null;

    default:
      return null;
  }
};
